import { ApolloClient } from "@apollo/client";
import { MenuItem } from "@mui/material";
import { GraphQLFormattedError } from "graphql";
import { enqueueSnackbar } from "notistack";
import { ReactElement } from "react";
import { UseFormSetError } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import { MetadataFieldTypes, UserSchemaOutput } from "@stw/yup-validators";

import {
  BoolMetadataValueFragment,
  EnumMetadataValueFragment,
  EnumMultiMetadataValueFragment,
  MetadataBoolFieldFragment,
  MetadataEnumFieldFragment,
  MetadataEnumMultiFieldFragment,
  MetadataStringFieldFragment,
  StringMetadataValueFragment,
  MetadataUserListFieldFragment,
  UserListMetadataValueFragment,
  MetadataStateFieldFragment,
  StateMetadataValueFragment,
  StateMultiMetadataValueFragment,
} from "~frontend/__generated__/graphql";
import Select from "~frontend/components/forms/FormSelect";

export const ERROR_SAVING_MSG = "Error saving changes.";
export const NO_VALUE = "-";

export type ApiMetadataFieldValue =
  | BoolMetadataValueFragment
  | EnumMetadataValueFragment
  | StringMetadataValueFragment
  | EnumMultiMetadataValueFragment
  | UserListMetadataValueFragment
  | StateMetadataValueFragment
  | StateMultiMetadataValueFragment;

export const mapTypenameToLabel = (
  typeName:
    | "CampaignMetadataBoolField"
    | "CampaignMetadataEnumField"
    | "CampaignMetadataEnumMultiField"
    | "CampaignMetadataStringField"
    | "CampaignMetadataUserListField"
    | "CampaignMetadataStateField"
    | undefined
): string => {
  switch (typeName) {
    case "CampaignMetadataBoolField":
      return "True/False";
    case "CampaignMetadataStringField":
      return "Text";
    case "CampaignMetadataEnumField":
      return "Single Select";
    case "CampaignMetadataEnumMultiField":
      return "Multi Select";
    case "CampaignMetadataUserListField":
      return "User List";
    case "CampaignMetadataStateField":
      return "State List";
    default:
      return null as never;
  }
};

export function mapValueTypenameToFieldType(
  typeName:
    | "CampaignMetadataBooleanValue"
    | "CampaignMetadataStringValue"
    | "CampaignMetadataEnumValue"
    | "CampaignMetadataEnumMultiValue"
    | "CampaignMetadataBoolField"
    | "CampaignMetadataEnumMultiField"
    | "CampaignMetadataEnumField"
    | "CampaignMetadataStringField"
    | "CampaignMetadataUserListField"
    | "CampaignMetadataUserListValue"
    | "CampaignMetadataStateField"
    | "CampaignMetadataStateValue"
    | "CampaignMetadataStateMultiValue"
): MetadataFieldTypes {
  switch (typeName) {
    case "CampaignMetadataBoolField":
    case "CampaignMetadataBooleanValue":
      return MetadataFieldTypes.BOOLEAN;
    case "CampaignMetadataStringField":
    case "CampaignMetadataStringValue":
      return MetadataFieldTypes.STRING;
    case "CampaignMetadataEnumField":
    case "CampaignMetadataEnumValue":
      return MetadataFieldTypes.ENUM;
    case "CampaignMetadataEnumMultiField":
    case "CampaignMetadataEnumMultiValue":
      return MetadataFieldTypes.ENUM_MULTI;
    case "CampaignMetadataUserListField":
    case "CampaignMetadataUserListValue":
      return MetadataFieldTypes.USER_LIST;
    case "CampaignMetadataStateField":
    case "CampaignMetadataStateValue":
      return MetadataFieldTypes.STATE;
    case "CampaignMetadataStateMultiValue":
      return MetadataFieldTypes.STATE_MULTI;
    default:
      return null as never;
  }
}

export function SelectFieldType({
  value,
  onChange,
  disabled = false,
}: {
  value: MetadataFieldTypes;
  onChange: (type: MetadataFieldTypes) => void;
  disabled?: boolean;
}): ReactElement {
  return (
    <Select
      value={value}
      label="Field Type"
      disabled={disabled}
      onChange={event => onChange(event.target.value as MetadataFieldTypes)}
    >
      <MenuItem value={MetadataFieldTypes.STRING}>Text</MenuItem>
      <MenuItem value={MetadataFieldTypes.BOOLEAN}>True / False</MenuItem>
      <MenuItem value={MetadataFieldTypes.ENUM}>Single Select</MenuItem>
      <MenuItem value={MetadataFieldTypes.ENUM_MULTI}>Multi Select</MenuItem>
      <MenuItem value={MetadataFieldTypes.USER_LIST}>User List</MenuItem>
      <MenuItem value={MetadataFieldTypes.STATE}>State List</MenuItem>
    </Select>
  );
}

export const handlePostSubmit = (options: {
  onSuccess: () => void;
  setError: UseFormSetError<any>;
  apolloClient: ApolloClient<object>;
  isNewField: boolean;
  data?:
    | MetadataBoolFieldFragment
    | MetadataStringFieldFragment
    | MetadataEnumFieldFragment
    | MetadataEnumMultiFieldFragment
    | MetadataUserListFieldFragment
    | MetadataStateFieldFragment
    | undefined;
  errors: readonly GraphQLFormattedError[] | undefined;
}): void => {
  const { onSuccess, setError, data, errors } = options;
  if (errors) {
    if (
      errors.some(gqlError => gqlError.extensions?.code === "DUPLICATE_RECORD")
    ) {
      setError("name", {
        type: "manual",
        message: "A field with this name already exists.",
      });
    } else {
      enqueueSnackbar(ERROR_SAVING_MSG, { variant: "error" });
    }
    return;
  }

  if (!data) {
    // This shouldn't happen but needed to make TypeScript happy.
    return;
  }

  onSuccess();
};

export function createEmptySelectValue(): { key: string; value: string } {
  return {
    key: uuidv4(),
    value: "",
  };
}

export function getValueFromCampaignMetadata(
  field: ApiMetadataFieldValue
): string | boolean | string[] | UserSchemaOutput | null | undefined {
  switch (field.__typename) {
    case "CampaignMetadataStateValue":
    case "CampaignMetadataStringValue":
    case "CampaignMetadataEnumValue":
      return field.stringValue;
    case "CampaignMetadataBooleanValue":
      return field.boolValue;
    case "CampaignMetadataStateMultiValue":
    case "CampaignMetadataEnumMultiValue":
      return field.stringListValue;
    case "CampaignMetadataUserListValue": {
      const { userId, fullName } =
        field.userListValue as UserListMetadataValueFragment["userListValue"];
      return { userId, fullName };
    }
    default:
      return null;
  }
}

export function getDefaultValueFromCampaignMetadata(
  field: ApiMetadataFieldValue
): string | boolean | string[] | UserSchemaOutput | null | undefined {
  switch (field.__typename) {
    case "CampaignMetadataStringValue":
      return field.defaultStringValue;
    case "CampaignMetadataBooleanValue":
      return field.defaultBoolValue;
    case "CampaignMetadataEnumValue":
      return field.defaultStringValue;
    case "CampaignMetadataEnumMultiValue":
      return field.defaultStringListValue;
    case "CampaignMetadataStateValue":
      return field.defaultStringValue;
    case "CampaignMetadataStateMultiValue":
      return field.defaultStringListValue;
    case "CampaignMetadataUserListValue": {
      const { userId, fullName } = field.defaultUserListValue;
      return { userId, fullName };
    }
    default:
      return null;
  }
}

export function mapNameAndValueFromCampaignMetadata(
  field: ApiMetadataFieldValue
): {
  name: string;
  value: string;
} {
  const { name } = field;
  switch (field.__typename) {
    case "CampaignMetadataUserListValue": {
      const { fullName } = field.userListValue;
      return { name, value: fullName === "" ? NO_VALUE : fullName };
    }
    case "CampaignMetadataStateMultiValue":
    case "CampaignMetadataEnumMultiValue":
      if (field.stringListValue.length === 0) {
        return { name, value: NO_VALUE };
      }
      return { name, value: field.stringListValue.join(", ") };
    case "CampaignMetadataStateValue":
    case "CampaignMetadataEnumValue":
    case "CampaignMetadataStringValue": {
      const { stringValue } = field;
      return { name, value: stringValue !== "" ? stringValue : NO_VALUE };
    }
    case "CampaignMetadataBooleanValue": {
      const value = field.boolValue;
      if (value === null) {
        return { name, value: NO_VALUE };
      } else {
        return { name, value: value ? "True" : "False" };
      }
    }
  }
}
